import type { ServicePageData } from './types';

const cafeBarriers: ServicePageData = {
  slug: 'cafe-barriers',
  eyebrow: 'Display',
  eyebrowTo: '/services/all-display',
  title: 'Mark out your space with',
  titleAccent: 'branded cafe barriers.',
  intro:
    'Post-and-banner barrier sets define a pavement seating area, a queue line or an event perimeter, and turn the boundary itself into advertising space. Printed panels slot onto freestanding posts with weighted bases, so the whole run goes up and down in minutes.',
  heroImage: '/home/parasols/parasol-hero.jpg',

  useCases: [
    { title: 'Pavement cafes and bars', text: 'Mark out your licensed seating area and keep it looking tidy and official.' },
    { title: 'Queue management', text: 'Guide customers into an orderly line outside a busy shop front or event entrance.' },
    { title: 'Event perimeters', text: 'Section off a stage area, VIP space or car park without scaffolding or fencing.' },
    { title: 'Restaurant and hotel forecourts', text: 'A smart, branded boundary between your frontage and the pavement.' },
    { title: 'Markets and pop-up stalls', text: 'Define your pitch and stop customers wandering behind the counter.' },
  ],

  optionsHeading: 'Choose your barrier set',
  optionsIntro: 'Posts, bases and banners are sold as a system, so every run matches and slots together.',
  options: [
    {
      id: 'single-sided',
      title: 'Single-sided banner',
      lead: 'The economical option where the barrier only faces one way.',
      body: 'Printed on one side and plain on the reverse. Suits a run against a wall or window where only the outward face is seen.',
      image: '/home/parasols/hodgehill-parasol.jpg',
      specs: [
        { label: 'Print', value: 'Full colour on the outward-facing side only.' },
        { label: 'Fixing', value: 'Banner slots between two posts, tensioned top and bottom.' },
        { label: 'Best for', value: 'Runs against a wall, window or fixed backdrop.' },
      ],
    },
    {
      id: 'double-sided',
      title: 'Double-sided banner',
      lead: 'For a barrier that stands away from any wall and is seen from both sides.',
      body: 'The same design or two different designs printed on each face, so the barrier reads correctly whichever direction it is approached from.',
      image: '/home/parasols/wildjac-parasol.jpg',
      specs: [
        { label: 'Print', value: 'Full colour on both faces, matching or different artwork.' },
        { label: 'Fixing', value: 'Banner slots between two posts, tensioned top and bottom.' },
        { label: 'Best for', value: 'A freestanding line out in the open, such as a queue or event perimeter.' },
      ],
    },
    {
      id: 'posts-bases',
      title: 'Post and base sets',
      lead: 'The frame that holds each banner section upright.',
      body: 'Weighted bases keep the run stable outdoors without pegging into the ground, so the whole barrier can be repositioned or packed away at the end of the day.',
      image: '/home/parasols/petal-bar-parasol.jpg',
      specs: [
        { label: 'Base', value: 'Weighted, freestanding. No fixing into paving or ground needed.' },
        { label: 'Posts', value: 'Slot into the base, spaced to suit your banner width.' },
        { label: 'Layout', value: 'Straight runs or corners, built up post by post to the length you need.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Printed vinyl banner',
      text: 'Full-colour artwork printed on our in-house wide-format printer, including photos, gradients and fine detail.',
    },
    {
      title: 'Branded panels only',
      text: 'Supply your own posts and bases and order replacement banner panels to refresh the branding.',
    },
  ],

  gallery: [
    '/home/parasols/parasol-hero.jpg',
    '/home/parasols/hodgehill-parasol.jpg',
    '/home/parasols/hodgehill-parasol-2.jpg',
    '/home/parasols/wildjac-parasol.jpg',
    '/home/parasols/wildjac-2.jpg',
    '/home/parasols/petal-bar-parasol.jpg',
    '/home/parasols/nothing-bound-parasol-2.jpg',
  ],

  info: [
    {
      title: 'Supply only',
      text: 'Post, base and banner sets arrive ready to assemble by hand, no tools required.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Care',
      text: 'Wipe the banners down with soapy water and bring the set indoors overnight where possible to keep the print looking sharp for longer.',
    },
    {
      title: 'Turnaround',
      text: 'Production is 5 to 10 working days from approval. Free design mockup and quote before you commit to anything.',
    },
  ],

  faqs: [
    {
      q: 'Do the barriers need fixing to the ground?',
      a: 'No. The bases are weighted and freestanding, so the whole run can be moved or packed away at the end of the day.',
    },
    {
      q: 'Can I order more sections later to extend a run?',
      a: 'Yes, keep us the design file and order numbers and we can match up additional post and banner sections.',
    },
    {
      q: 'Can I use my own posts with new banners?',
      a: 'In most cases yes, as long as the post spacing matches. Send us a photo or measurements and we will confirm.',
    },
  ],

  cta: {
    title: 'Get your free cafe barrier quote and mockup.',
    text: 'Tell us the length of run you need and we will recommend single or double-sided banners and the right base.',
    image: '/home/parasols/parasol-mockup.jpg',
  },
};

export default cafeBarriers;
